import { chargeItemAction, imagingAction, labAction, procedureAction, ProductInventoryAction, roomAction } from '@api'
import { useHmoTarrif } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { HmoTariffAttr } from '@type/hmo.type'
import { FormButton, FormInputField, Modal, SelectInputField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { Box } from 'grommet'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { HmoStatusOptions } from './AddHmo'

const schema = yup.object({
  tariffable_type: yup
    .string()
    .oneOf(['LabTest', 'ProductInventory', 'Procedure', 'Imaging', 'Room', 'ChargeItem'], 'Invalid tariffable type')
    .required('Tariffable type is required'),
  tariffable_id: yup
    .number()
    .integer('ID must be an integer')
    .positive('ID must be greater than 0')
    .required('Tariffable ID is required'),
  price: yup.string().required('Price is required'),
  status: yup.string().oneOf(['active', 'inactive'], 'Invalid status').required('Status is required'),
  code: yup.string().trim().nullable().notRequired()
})
type Props = {
  onClose: () => void
  id?: string
  defaultData?: HmoTariffAttr
  hmoId?: string
  planId?: string
}

const tariffableOptions = [
  { label: 'Lab test', value: 'LabTest' },
  { label: 'Medication', value: 'ProductInventory' },
  { label: 'Procedure', value: 'Procedure' },
  { label: 'Imaging', value: 'Imaging' },
  { label: 'Room', value: 'Room' },
  { label: 'ChargeItem', value: 'ChargeItem' }
]

export const EditTariff = ({ defaultData, onClose, id }: Props) => {
  const methods = useForm({
    resolver: yupResolver(schema),
    values: {
      ...defaultData,
      tariffable_id: defaultData?.tariffable_id,
      tariffable_type: defaultData?.tariffable_type || 'ProductInventory',
      price: defaultData?.price?.toString() || ''
    } as unknown as yup.InferType<typeof schema>
  })
  const { handleSubmit, formState, watch } = methods
  const tariffableType = watch('tariffable_type') || 'ProductInventory'
  const { updateTarrif } = useHmoTarrif()

  const loadOptionsMap: Record<string, any> = {
    ChargeItem: chargeItemAction,
    Imaging: imagingAction,
    LabTest: labAction,
    Procedure: procedureAction,
    ProductInventory: ProductInventoryAction,
    Room: roomAction
  }
  const onSubmit = async (formRecObj: yup.InferType<typeof schema>) => {
    const payload = {
      data: {
        type: 'hmo_tariffs',
        attributes: {
          ...formRecObj
        }
      }
    }

    try {
      await updateTarrif({ data: payload, id: id || '' })
      showToast(`Tariff updated successfully`)
      onClose()
    } catch (error) {
      getError(error)
    }
  }
  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Edit Tariff">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            className="w-full max-w-[600px]"
            flex={false}
            gap={'20px'}
            overflow="auto"
            pad={{ horizontal: '32px', vertical: '16px' }}
          >
            <Controller
              control={methods.control}
              key={tariffableType}
              name="tariffable_id"
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Select
                  borderRadius={'100px'}
                  defaultOptions
                  defaultValue={{ label: defaultData?.tariffable_name, value: defaultData?.tariffable_id }}
                  error={error?.message}
                  getOptionValue={({ value }) => value}
                  isAsync
                  label="Service"
                  loadOptions={loadOptionsMap[tariffableType]}
                  onChange={(val: any) => onChange(val?.value)}
                />
              )}
            />
            <SelectInputField
              label="Status"
              labelKey={({ label }) => label}
              name="status"
              options={HmoStatusOptions}
              valueKey={{ key: 'value', reduce: true }}
            />
            <div className="">
              <FormInputField label="Price" name="price" type="number" />
            </div>
          </Box>
          <FormButton isLoading={formState.isSubmitting} onClose={onClose} submit="Save" />
        </form>
      </Modal>
    </FormProvider>
  )
}
